import React from "react";
import {
    Card,
    CardContent,
    Typography,
    Stack,
    Divider,
    Box,
} from "@mui/material";

// Import honeycomb options for proper key matching
import honeycombOptions from "../../../json/honeycombOptions.json";

// Function to calculate sheets needed for waveguides (open orders only)
function calculateSheetsNeeded(honeycombBreakdown, rawWaveguideData = []) {
    const SHEET_WIDTH = 36; // inches
    const SHEET_HEIGHT = 24; // inches

    let totalSheetsNeeded = 0;
    const breakdown = {};

    // Helper function to match honeycomb types (same as in WaveguideOrderList)
    function getHoneycombTypeKey(honeycombType) {
        if (
            !honeycombType ||
            (!honeycombType.cell && !honeycombType.thickness)
        ) {
            return "unknown";
        }

        // Find matching honeycomb option
        const matchingOption = honeycombOptions.find(
            (option) =>
                Math.abs(option.cell - (honeycombType.cell || 0)) < 0.001 &&
                Math.abs(option.thickness - (honeycombType.thickness || 0)) <
                    0.001
        );

        if (matchingOption) {
            return `${matchingOption.cell}_${matchingOption.thickness}_${matchingOption.material}`;
        }

        // If no exact match found, create a key from the available data
        return `${honeycombType.cell || "unknown"}_${honeycombType.thickness ||
            "unknown"}_unknown`;
    }

    // Function to calculate how many pieces fit on a sheet
    function calculatePiecesPerSheet(width, height) {
        // Try both orientations and pick the one that fits more pieces
        const orientation1 = {
            piecesWide: Math.floor(SHEET_WIDTH / width),
            piecesHigh: Math.floor(SHEET_HEIGHT / height),
        };

        const orientation2 = {
            piecesWide: Math.floor(SHEET_WIDTH / height),
            piecesHigh: Math.floor(SHEET_HEIGHT / width),
        };

        const piecesPerSheet1 =
            orientation1.piecesWide * orientation1.piecesHigh;
        const piecesPerSheet2 =
            orientation2.piecesWide * orientation2.piecesHigh;

        return Math.max(piecesPerSheet1, piecesPerSheet2);
    }

    // Process each honeycomb type separately
    Object.entries(honeycombBreakdown).forEach(([key, data]) => {
        let totalSheetsForType = 0;
        let totalPiecesForType = 0;
        let totalAreaForType = 0;

        // Extract pieces from raw data for this honeycomb type (only open orders)
        if (rawWaveguideData && Array.isArray(rawWaveguideData)) {
            rawWaveguideData.forEach((order) => {
                // Only process orders that are not completed
                if (!order.completed) {
                    const orderHoneycombKey = getHoneycombTypeKey(
                        order.honeycomb_type
                    );

                    if (
                        orderHoneycombKey === key &&
                        order.list &&
                        Array.isArray(order.list)
                    ) {
                        order.list.forEach((item) => {
                            let width = 0;
                            let height = 0;

                            // Handle different size formats
                            if (item.size) {
                                // If it's round (has diameter), treat it as a square
                                if (item.size.diameter) {
                                    width = item.size.diameter;
                                    height = item.size.diameter;
                                }
                                // If it's rectangular (has width and height)
                                else if (item.size.width && item.size.height) {
                                    width = item.size.width;
                                    height = item.size.height;
                                }
                            }

                            // Calculate sheets needed for this item
                            if (width > 0 && height > 0 && item.qty > 0) {
                                const piecesPerSheet = calculatePiecesPerSheet(
                                    width,
                                    height
                                );

                                let sheetsNeededForItem;
                                if (piecesPerSheet === 0) {
                                    // Piece is larger than sheet, needs multiple sheets per piece
                                    const orientation1 = {
                                        sheetsWide: Math.ceil(
                                            width / SHEET_WIDTH
                                        ),
                                        sheetsHigh: Math.ceil(
                                            height / SHEET_HEIGHT
                                        ),
                                    };
                                    const orientation2 = {
                                        sheetsWide: Math.ceil(
                                            height / SHEET_WIDTH
                                        ),
                                        sheetsHigh: Math.ceil(
                                            width / SHEET_HEIGHT
                                        ),
                                    };
                                    const sheetsPerPiece = Math.min(
                                        orientation1.sheetsWide *
                                            orientation1.sheetsHigh,
                                        orientation2.sheetsWide *
                                            orientation2.sheetsHigh
                                    );
                                    sheetsNeededForItem =
                                        sheetsPerPiece * item.qty;
                                } else {
                                    // Multiple pieces can fit on one sheet
                                    sheetsNeededForItem = Math.ceil(
                                        item.qty / piecesPerSheet
                                    );
                                }

                                totalSheetsForType += sheetsNeededForItem;
                                totalPiecesForType += item.qty;
                                totalAreaForType += width * height * item.qty;
                            }
                        });
                    }
                }
            });
        }

        if (totalPiecesForType === 0) {
            breakdown[key] = { sheets: 0, efficiency: 0, pieces: 0 };
            return;
        }

        const sheetArea = SHEET_WIDTH * SHEET_HEIGHT;
        const efficiency =
            totalSheetsForType > 0
                ? (totalAreaForType / (totalSheetsForType * sheetArea)) * 100
                : 0;

        breakdown[key] = {
            sheets: totalSheetsForType,
            efficiency: Math.round(efficiency * 10) / 10,
            pieces: totalPiecesForType,
            totalArea: totalAreaForType,
        };

        totalSheetsNeeded += totalSheetsForType;
    });

    return { total: totalSheetsNeeded, breakdown };
}

export default function WaveguideSummary(props) {
    const {
        darkState,
        title = "WAVEGUIDES",
        totalCount,
        openCount,
        totalQty,
        openQty,
        totalArea,
        openArea,
        honeycombBreakdown = {},
        details = [],
        rawWaveguideData = [], // Add this prop to get the raw data for calculations
    } = props;

    // Calculate sheets needed
    const sheetsCalculation = calculateSheetsNeeded(
        honeycombBreakdown,
        rawWaveguideData
    );

    return (
        <Card
            variant="outlined"
            sx={{
                my: 1,
                mb: 3,
                width: "100%",
                border: darkState ? "1.5px solid" : "3px solid",
                borderColor: "primary.main",
                borderRadius: 2,
            }}
        >
            <CardContent
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                }}
            >
                <Typography
                    variant="h3"
                    color="text.primary"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: "2rem", sm: "2.5rem" }, // Larger font size
                        textAlign: "center",
                    }}
                >
                    {title}
                </Typography>
                <Divider sx={{ my: 1, width: "100%" }} />
                <Stack
                    direction="row"
                    spacing={{ xs: 2, sm: 4 }}
                    sx={{ mt: 1, mb: 2 }}
                >
                    <Stack alignItems="center">
                        <Typography
                            variant="h6"
                            color="text.primary"
                            sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
                        >
                            Total {(totalCount || 0) === 1 ? "Order" : "Orders"}
                        </Typography>
                        <Typography
                            variant="h4"
                            color="text.secondary"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: "1.75rem", sm: "2.125rem" },
                            }}
                        >
                            {totalCount || 0}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                            ({totalQty || 0}{" "}
                            {(totalQty || 0) === 1 ? "waveguide" : "waveguides"}
                            )
                        </Typography>
                    </Stack>
                    <Divider orientation="vertical" flexItem />

                    <Stack alignItems="center">
                        <Typography
                            variant="h6"
                            color="warning.main"
                            sx={{ fontSize: { xs: "0.875rem", sm: "1.25rem" } }}
                        >
                            Open {(openCount || 0) === 1 ? "Order" : "Orders"}
                        </Typography>
                        <Typography
                            variant="h4"
                            color="warning.main"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: "1.75rem", sm: "2.125rem" },
                            }}
                        >
                            {openCount || 0}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="warning.main"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                            ({openQty || 0}{" "}
                            {(openQty || 0) === 1 ? "waveguide" : "waveguides"})
                        </Typography>
                    </Stack>
                </Stack>

                {/* Honeycomb Type Breakdown */}
                {Object.keys(honeycombBreakdown).length > 0 && (
                    <>
                        <Divider sx={{ my: 2, width: "100%" }} />
                        <Typography
                            variant="h6"
                            color="text.primary"
                            gutterBottom
                            sx={{
                                fontSize: { xs: "1rem", sm: "1.25rem" },
                                fontWeight: 600,
                                textAlign: "center",
                            }}
                        >
                            Honeycomb
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(auto-fit, minmax(200px, 1fr))",
                                },
                                gap: 2,
                                width: "100%",
                                mt: 1,
                            }}
                        >
                            {Object.entries(honeycombBreakdown).map(
                                ([key, data]) => (
                                    <Box
                                        key={key}
                                        sx={{
                                            p: 2,
                                            border: 1,
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            backgroundColor:
                                                "background.default",
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            color="text.primary"
                                            sx={{
                                                fontWeight: 600,
                                                textAlign: "center",
                                                mb: 1,
                                                fontSize: {
                                                    xs: "0.75rem",
                                                    sm: "0.875rem",
                                                },
                                            }}
                                        >
                                            {data.label}
                                        </Typography>
                                        <Stack
                                            spacing={0.5}
                                            alignItems="center"
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: {
                                                        xs: "0.65rem",
                                                        sm: "0.75rem",
                                                    },
                                                }}
                                            >
                                                Total:{" "}
                                                {data.totalArea.toLocaleString()}{" "}
                                                sq in
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: {
                                                        xs: "0.65rem",
                                                        sm: "0.75rem",
                                                    },
                                                }}
                                            >
                                                Perimeter:{" "}
                                                {(
                                                    data.totalPerimeter || 0
                                                ).toLocaleString()}{" "}
                                                in
                                            </Typography>
                                            {data.openArea > 0 && (
                                                <Typography
                                                    variant="body2"
                                                    color="warning.main"
                                                    sx={{
                                                        fontSize: {
                                                            xs: "0.65rem",
                                                            sm: "0.75rem",
                                                        },
                                                    }}
                                                >
                                                    Open:{" "}
                                                    {data.openArea.toLocaleString()}{" "}
                                                    sq in
                                                </Typography>
                                            )}
                                            {data.openPerimeter > 0 && (
                                                <Typography
                                                    variant="body2"
                                                    color="warning.main"
                                                    sx={{
                                                        fontSize: {
                                                            xs: "0.65rem",
                                                            sm: "0.75rem",
                                                        },
                                                    }}
                                                >
                                                    Open Perimeter:{" "}
                                                    {(
                                                        data.openPerimeter || 0
                                                    ).toLocaleString()}{" "}
                                                    in
                                                </Typography>
                                            )}
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    fontSize: {
                                                        xs: "0.6rem",
                                                        sm: "0.65rem",
                                                    },
                                                }}
                                            >
                                                {data.totalOrders}{" "}
                                                {data.totalOrders === 1
                                                    ? "order"
                                                    : "orders"}{" "}
                                                ({data.totalQty} qty)
                                            </Typography>
                                            {sheetsCalculation.breakdown[
                                                key
                                            ] && (
                                                <Stack
                                                    spacing={0.5}
                                                    alignItems="center"
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="info.main"
                                                        sx={{
                                                            fontSize: {
                                                                xs: "0.6rem",
                                                                sm: "0.65rem",
                                                            },
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {
                                                            sheetsCalculation
                                                                .breakdown[key]
                                                                .sheets
                                                        }{" "}
                                                        {sheetsCalculation
                                                            .breakdown[key]
                                                            .sheets === 1
                                                            ? "sheet"
                                                            : "sheets"}{" "}
                                                        needed
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="info.main"
                                                        sx={{
                                                            fontSize: {
                                                                xs: "0.6rem",
                                                                sm: "0.65rem",
                                                            },
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {
                                                            sheetsCalculation
                                                                .breakdown[key]
                                                                .efficiency
                                                        }
                                                        % efficient with 36" x
                                                        24" sheets
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Box>
                                )
                            )}
                        </Box>
                    </>
                )}

                {details
                    .filter((text) => Boolean(text))
                    .map((text, index) => (
                        <Typography
                            key={`waveguide-summary-detail-${index}`}
                            variant="caption"
                            color="text.secondary"
                            gutterBottom
                        >
                            {text}
                        </Typography>
                    ))}
            </CardContent>
        </Card>
    );
}
